import { use, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ButtonTextLoader from '../../../components/Loaders/ButtonTextLoader';
import CreateProjectDropdown from '../../../components/DropdownStyle/CreateProjectDropdown';
import ComponentCircleLoader from '../../../components/Loaders/ComponentCircleLoader';
import ReactSelectDropdown from '../../../components/ReactSelectDropdown';
import { filterOption } from '../../../components/ReactSelectDropdown/ReactSelectDropdown';
import {
  CommonFlex,
  CommonGrid,
  CommonText,
  Container,
  FilterLabel,
  InputWithLabelSection,
  PageTitle,
  VerticalBarTrack,
} from '../../../styledComponents/common';
import { ButtonIcon, WhiteButtonWithIcon } from '../../../styledComponents/buttons';
import excelIcon from '../../../assets/img/icons/excel-green.svg';
import { FlexTab, TabButtons, VerticalBar } from '../dashboardStyles';
import CampReadinessChart from './CampReadinessChart';
import CircularProgressBar from './CircularProgressBar';

const CampReadinessNew = ({
  officesShortList,
  itemsList,
  getOfficesShortList,
  getItemsList,
  campReadinessDashboardData = [],
  getCampReadinessDashboardData,
  isLoading,
  // public zones
  publicZoneList,
  getPublicZoneList,
  // export
  exportOfficeSubtaskReport,
  exportOfficeSubtaskReportLoading,
}) => {
  const { t } = useTranslation();

  const [selectedTab, setSelectedTab] = useState('Mina');
  const [selectedOffice, setSelectedOffice] = useState({
    label: t('all_offices'),
    value: null,
  });

  const [officeOptions, setOfficeOptions] = useState([
    { label: t('all_offices'), value: null },
  ]);
  const [loadingOfficeId, setLoadingOfficeId] = useState(null);

  useEffect(() => {
    getOfficesShortList();
    getPublicZoneList();
  }, []);

  useEffect(() => {
    if (!exportOfficeSubtaskReportLoading) {
      setLoadingOfficeId(null);
    }
  }, [exportOfficeSubtaskReportLoading]);


  const averageReadiness = useMemo(() => {
    if (!campReadinessDashboardData?.length) return 0;

    const totalReadiness = campReadinessDashboardData.reduce(
      (sum, office) => sum + office.readiness_percentage,
      0,
    );
    return Math.round(totalReadiness / campReadinessDashboardData.length);
  }, [campReadinessDashboardData]);

  const handleExportAllSubtaskPublicZoneReport = (office) => {
    console.log('Exporting report for office:', office);
    if (loadingOfficeId) return;
    const payload = {};
    // Get public zone ID from publicZoneList based on selectedTab
    const publicZone = publicZoneList?.find(zone => zone.name === selectedTab);
    if (publicZone?.id) {
      payload.public_zone_id = publicZone.id;
    }
    if (office?.office_id) {
      payload.office_id = office.office_id;
      payload.readiness_percentage = office.readiness_percentage;
    }
    setLoadingOfficeId(office?.office_id ?? null);
    exportOfficeSubtaskReport(payload);
  };

  const isExportDisabled = () => {
    const publicZone = publicZoneList?.find(zone => zone.name === selectedTab);
    return !publicZone?.id;
  };

  useEffect(() => {
    const payload = {
      public_zone_id: publicZoneList?.find(zone => zone.name === selectedTab)?.id,
    };
    if (selectedOffice?.value) {
      payload.office_id = selectedOffice.value;
    }
    getCampReadinessDashboardData(payload);
  }, [selectedOffice, selectedTab, publicZoneList]);

  useEffect(() => {
    if (officesShortList?.length) {
      const options = [
        { label: t('all_offices'), value: null },
        ...officesShortList.map(office => ({
          label: office.name,
          value: office.id,
        })),
      ];
      setOfficeOptions(options);
    }
  }, [officesShortList]);

  return (
    <>
      {/* Filters */}
      <CommonFlex gap="20px">
        <FlexTab>
          <TabButtons
            active={selectedTab == 'Mina'}
            onClick={() => setSelectedTab('Mina')}
          >
            {t('Mina')}
          </TabButtons>
          <TabButtons
            active={selectedTab == 'Arafat'}
            onClick={() => setSelectedTab('Arafat')}
          >
            {t('Arafat')}
          </TabButtons>
          <TabButtons
            active={selectedTab == 'Muzdalifah'}
            onClick={() => setSelectedTab('Muzdalifah')}
          >
            {t('Muzdalifah')}
          </TabButtons>
        </FlexTab>
        <VerticalBar />
        <InputWithLabelSection width="200px">
          <ReactSelectDropdown
            isSearchable={false}
            value={selectedOffice}
            options={officeOptions}
            placeholder={t('select_office')}
            onChange={setSelectedOffice}
            styles={CreateProjectDropdown()}
            filterOption={filterOption}
          />
        </InputWithLabelSection>
        <CommonFlex gap="12px">
          <span
            style={{
              fontSize: '12px',
              color: '#9ca3af',
            }}
          >
            Avg Readiness
          </span>
          <CircularProgressBar
            percentage={averageReadiness}
            color={
              averageReadiness >= 60
                ? '#16a34a'
                : averageReadiness >= 30
                  ? '#eab308'
                  : '#c06040'
            }
            size="54"
            strokeWidth={'6'}
          />
        </CommonFlex>
      </CommonFlex>
      {isLoading ? (
        <ComponentCircleLoader />
      ) : (
        <CommonGrid gap="20px" columns="1fr 1fr 1fr 1fr">
          {campReadinessDashboardData?.length == 0 ? (
            <CommonFlex justifyContent="center" margin="30px 0">
              <CommonText>{t('no_data_available')}</CommonText>
            </CommonFlex>
          ) : (
            <>
              {campReadinessDashboardData?.map((office, index) => (
                <InputWithLabelSection
                  padding="16px"
                  style={{
                    backgroundColor: '#fafafa',
                    border: '1px solid #f3f4f6',
                    borderRadius: '12px',
                  }}
                >
                  <CommonGrid columns="1fr 1fr" alignItems="center" gap="12px">
                    <CommonText bold name fontSize="16px">
                      {office?.office_name}
                    </CommonText>
                    <CommonFlex justifyContent="flex-end">
                      <WhiteButtonWithIcon
                        onClick={() => handleExportAllSubtaskPublicZoneReport(office)}
                        color="#19AC65"
                        borderColor="#19AC65"
                        hoverBorderColor="#129652"
                        padding={(exportOfficeSubtaskReportLoading && loadingOfficeId === office.office_id) ? '0 20px' : '0 25px'}
                        disabled={(exportOfficeSubtaskReportLoading && loadingOfficeId === office.office_id) || isExportDisabled()}
                        style={{ width: '100px', height: '10px' }}
                        height="20px"
                      >
                        {(exportOfficeSubtaskReportLoading && loadingOfficeId === office.office_id) ? (
                          <ButtonTextLoader color="#19AC65" fontSize='12px' loadingText=" " size={6} />
                        ) : (
                          <>
                            <ButtonIcon src={excelIcon} size="16px" />
                            {t('export')}
                          </>
                        )}
                      </WhiteButtonWithIcon>
                    </CommonFlex>
                  </CommonGrid>
                  <CommonFlex justifyContent="center">
                    <CircularProgressBar
                      percentage={office?.readiness_percentage}
                      color={
                        office?.readiness_percentage >= 60
                          ? '#16a34a'
                          : office?.readiness_percentage >= 30
                            ? '#eab308'
                            : '#c06040'
                      }
                      size="96"
                    />
                  </CommonFlex>
                </InputWithLabelSection>
              ))}
            </>
          )}
        </CommonGrid>
      )}
    </>
  );
};

export default CampReadinessNew;
